import { useNotificationToast } from '@/lib/hooks/useNotificationToast';
import { ChilliFileUpload } from '@group-gti/chilli-design-system-react';
import React from 'react';
import patchUserAction from '../../app/redux/actions/patchUserAction';
import { useAppDispatch } from '../../app/redux/hooks';
import { getUserFile } from '../../app/services/user';
import { FieldInfo, FieldValue } from '../../app/user/profile/profileUtils';

type Props = {
  field: FieldInfo;
  userData: FieldValue;
};

/**
 * FileField Component
 * @param {object} userData
 * @param {object} field
 * @return {object}
 */
const FileField = ({ field, userData }: Props) => {
  const dispatch = useAppDispatch();
  const { showNotificationToast } = useNotificationToast();
  const readFile = async (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function (event) {
        // The file's text will be printed here
        resolve(event.target?.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const onFileChange = async (e: any) => {
    let file = null;
    if (e.detail.files && e.detail.files[0]) {
      file = e.detail.files[0];
      if (file.size / 1024 / 1024 > 10) {
        showNotificationToast({
          type: 'error',
          message: 'Please upload a file which is less than 10mb.'
        });
        return;
      }
      const fileString: any = await readFile(file);
      const data = {
        profile: { [field.fieldGroup]: { [field.name]: { fileName: file.name, content: fileString.split(',')[1] } } }
      };
      const response = await dispatch(patchUserAction(data));
      if (response.success === true) {
        showNotificationToast({
          type: 'success',
          message: 'Your file has been successfully uploaded.'
        });
      } else {
        showNotificationToast({
          type: 'error',
          message: 'Sorry, something went wrong.'
        });
      }
    }
  };

  const onFileDelete = async (name: any) => {
    const arr = name.split('.');
    let values;
    let o = (values = {});
    arr.forEach((key: any) => {
      o = (o as any)[key] = {};
    });
    const response = await dispatch(
      patchUserAction({
        profile: {
          [field.fieldGroup]: {
            [field.name]: {}
          }
        }
      })
    );
    if (response.success === true) {
      showNotificationToast({
        type: 'success',
        message: 'Your file has been successfully deleted.'
      });
    } else {
      showNotificationToast({
        type: 'error',
        message: 'Sorry, something went wrong.'
      });
    }
  };

  const onFileDownload = async (id: any, values: any) => {
    let blob;
    let fileName;
    let fileType;
    if (values && values[0]) {
      const fileString = await readFile(values[0]);
      blob = await (await fetch(fileString)).blob();
      fileName = values[0].name;
      fileType = values[0].type;
    }
    // Prefilled file not submitted file so need id handling
    else if (id) {
      const response = await getUserFile(id);
      if (!response.success) {
        showNotificationToast({
          type: 'error',
          message: 'Sorry, something went wrong.'
        });
        return;
      }
      blob = await (await fetch(`data:${response.data.content_type};base64,${response.data.base64_content}`)).blob();
      fileName = response?.data.file_name;
      fileType = response?.data.content_type;
    } else {
      return;
    }
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([blob], { type: fileType }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    // Append to html page
    document.body.appendChild(link);
    // Force download
    link.click();
    // Clean up and remove the link
    link.parentNode?.removeChild(link);
  };

  return (
    <>
      <ChilliFileUpload
        identifier={field.name}
        label={field.label}
        fileName={userData?.rawValue?.fileName ? userData?.rawValue.fileName : ''}
        buttonText={userData?.rawValue ? 'Update' : 'Upload'}
        description={field.description}
        onChilliChange={(e: any) => {
          if (e.detail.files && e.detail.files[0]) {
            // input.onChange(e.detail.files);
            (e.target as HTMLElement).setAttribute('filename', e.detail.files[0].name);
          } else {
            // input.onChange(e.detail.files);
            (e.target as HTMLElement).removeAttribute('filename');
          }
          onFileChange(e);
        }}
        onChilliDelete={(e: any) => {
          (e.target as HTMLElement).setAttribute('filename', '');
          (e.target as HTMLElement).setAttribute('statetype', '');
          (e.target as HTMLElement).setAttribute('value', '');
          onFileDelete(userData?.rawValue.fileName);
        }}
        onChilliDownload={(e: any) => {
          onFileDownload(userData?.rawValue.id, e.detail.value);
        }}
      />
    </>
  );
};

export default FileField;
